/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { ImageUrlBuilder } from "@sanity/image-url/lib/types/builder";
import { FitMode } from "@sanity/image-url/lib/types/types";
import { isPresent } from "../utils/checks";
import { PartialSanityImageType, SanityImageType } from "./types";

export const SANITY_REF_PATTERN = /^image-([a-f\d]+)-(\d+x\d+)-(\w+)$/;

//type FitMode = undefined | "fillmax" | "max" | "min" | "clip";
type Dimensions = {
  width: number;
  height: number;
};

// TODO: review types from https://github.com/sanity-io/image-url/blob/master/src/types.ts
export type _SanityAsset = SanityImageType["asset"] & {
  _id: string;
  crop: SanityImageType["crop"];
  hotspot: SanityImageType["hotspot"];
};

export const DEFAULT_IMAGE_CONFIG = {
  auto: "format",
  fit: "max",
  quality: 75
};
export const isSanityImagePresent = (
  image: PartialSanityImageType | undefined
): image is SanityImageType => {
  return (
    !!image?.asset &&
    (isPresent(image.asset._id) || isPresent(image.asset["_ref"]))
  );
};

export const calculateCroppedDimensions = (
  image: SanityImageType,
  fitMode: FitMode = "crop"
): Dimensions => {
  const { dimensions } = parseSanityImageRef(image.asset._id);

  const origRatio = Math.round(dimensions.width / dimensions.height);
  //const width = dimensions.width;
  //const height = Math.round(width / origRatio);
  let cropRatio = origRatio;
  let maxWidth = dimensions.width;
  let maxHeight = dimensions.height;

  if (
    fitMode != "clip" &&
    "crop" in image &&
    image.crop &&
    Object.values(image.crop).some((n) => n && n > 0)
  ) {
    const cropWidth = Math.round(
      dimensions.width -
        (image.crop.left || 0) * dimensions.width -
        (image.crop.right || 0) * dimensions.width
    );
    const cropHeight = Math.round(
      dimensions.height -
        (image.crop.top || 0) * dimensions.height -
        (image.crop.bottom || 0) * dimensions.height
    );

    cropRatio = Math.round(cropWidth / cropHeight);
    if (cropRatio > origRatio) {
      maxHeight = cropHeight;
    } else {
      maxWidth = cropWidth;
    }
  }

  return {
    width: maxWidth,
    height: maxHeight
  };
};

// TODO: replace with https://github.com/sanity-io/image-url/blob/master/src/parseAssetId.ts
export const parseSanityImageRef = (
  id: string
): {
  assetId: string;
  dimensions: {
    width: number;
    height: number;
  };
  format: string;
} => {
  try {
    const [, assetId, dimensions, format] = SANITY_REF_PATTERN.exec(id) || [];
    const [width, height] = dimensions.split("x").map((v) => parseInt(v, 10));

    return {
      assetId,
      dimensions: { width, height },
      format
    };
  } catch {
    throw new Error(`Could not parse image ID "${id}"`);
  }
};

export const toSanityAsset = (
  image: SanityImageType
): _SanityAsset | undefined => {
  const { asset, hotspot, crop } = image;
  if (!asset) return undefined;

  const sanityAsset: _SanityAsset = {
    ...asset,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    _id: (asset._id || asset["_ref"])!,
    hotspot,
    crop
  };

  return sanityAsset;
};

export const getLocalizedAlt = (
  localizedAlt: { localized: string } | string | undefined
): string | undefined => {
  if (!localizedAlt) return;

  if (typeof localizedAlt !== "string" && "localized" in localizedAlt)
    return localizedAlt.localized;

  return localizedAlt as string;
};

export const buildSrc = (
  builder: ImageUrlBuilder,
  asset: _SanityAsset | SanityImageType["asset"],
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  { width, height, ...config }
): string => {
  const { dimensions } = parseSanityImageRef(asset._id);

  const origRatio = dimensions.width / dimensions.height;
  width = width || dimensions.width;
  height = height || Math.round(width / origRatio);

  return imageUrl(builder, asset, { ...config, width, height });
};

export const buildSrcSet = (
  builder: ImageUrlBuilder,
  asset: _SanityAsset | SanityImageType["asset"],
  config
): string => {
  const { dimensions } = parseSanityImageRef(asset._id);
  const fitMode = config.fit || DEFAULT_IMAGE_CONFIG.fit;

  // Determine dimensions and ratios for srcSet calculations
  const origRatio = dimensions.width / dimensions.height;
  const width = config.width || dimensions.width;
  const height = config.height || Math.round(width / origRatio);
  const targetRatio = width / height;
  let cropRatio = origRatio;
  let maxWidth = dimensions.width;
  let maxHeight = dimensions.height;

  // Compensate for dimensional changes if image was cropped in Sanity
  if (
    fitMode != "clip" &&
    "crop" in asset &&
    asset.crop &&
    Object.values(asset.crop).some((n) => n && n > 0)
  ) {
    const cropWidth =
      dimensions.width -
      (asset.crop.left || 0) * dimensions.width -
      (asset.crop.right || 0) * dimensions.width;
    const cropHeight =
      dimensions.height -
      (asset.crop.top || 0) * dimensions.height -
      (asset.crop.bottom || 0) * dimensions.height;

    cropRatio = cropWidth / cropHeight;
    if (cropRatio > origRatio) {
      maxHeight = cropHeight;
    } else {
      maxWidth = cropWidth;
    }
  }

  return Object.values(
    [0.5, 1, 1.5, 2, 3].reduce((set, dpr) => {
      const url = imageUrl(builder, asset, { ...config, dpr });
      const size = Math.round(
        // For modes where Sanity will not scale up, determine
        // the anticipated final width based on the params
        ["fillmax", "max", "min"].includes(fitMode)
          ? targetRatio < origRatio
            ? Math.min(
                (maxHeight / (height * dpr)) * (width * dpr),
                width * dpr
              )
            : Math.min(width * dpr, maxWidth)
          : width * dpr
      );

      // Avoid duplicate sizes in srcSet list
      if (!set[size]) {
        set[size] = `${url} ${size}w`;
      }
      return set;
    }, {})
  ).join(", ");
};

export const imageUrl = (
  builder: ImageUrlBuilder,
  asset: _SanityAsset | SanityImageType["asset"],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: Record<string, any> = {}
): string => {
  //fix for sanity:
  const { fit, width, height, ...otherParams } = params;
  let sanityParams = { fit, width, height: undefined };
  if (fit != "clip") sanityParams = { ...sanityParams, height }; //TODO: https://github.com/sanity-io/image-url/issues/32
  if (fit == "clip") sanityParams = { ...sanityParams, fit: "max" }; // HACK: issue with sanity CDN

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return Object.entries({
    ...DEFAULT_IMAGE_CONFIG,
    ...sanityParams,
    ...otherParams
  })
    .reduce(
      (acc, [key, value]) =>
        value
          ? Array.isArray(value)
            ? acc[key](...value)
            : acc[key](value)
          : acc,
      builder.image(asset)
    )
    .url()!
    .replace(/, /g, ",");
};
